import { useEffect, useRef, useState } from "react";
import { Paperclip, X } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg } from "../../lib/api";
import { Button } from "../ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../ui/dialog";
import { Input } from "../ui/input";
import { Label } from "../ui/label";
import { Textarea } from "../ui/textarea";
import { Checkbox } from "../ui/checkbox";

const PRIORITIES = [["rendah", "Rendah"], ["normal", "Normal"], ["tinggi", "Tinggi"], ["urgent", "URGENT"]];
const EMPTY = { title: "", detail: "", assignees: [], deadline: "", priority: "normal" };
const toLocal = (iso) => { if (!iso) return ""; const d = new Date(iso); const p = (n) => String(n).padStart(2, "0"); return `${d.getFullYear()}-${p(d.getMonth() + 1)}-${p(d.getDate())}T${p(d.getHours())}:${p(d.getMinutes())}`; };

export function TaskCreateDialog({ open, onClose, onCreated, task = null }) {
  const [admins, setAdmins] = useState([]);
  const [f, setF] = useState(EMPTY);
  const [files, setFiles] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileRef = useRef(null);
  const editing = !!task;
  useEffect(() => {
    if (!open) return;
    api.get("/admin-tasks/admins").then((r) => setAdmins(r.data)).catch(() => {});
    setF(task ? { title: task.title, detail: task.detail || "", assignees: task.assignees || [], deadline: toLocal(task.deadline), priority: task.priority || "normal" } : EMPTY);
    setFiles([]);
  }, [open, task]);
  const toggle = (id) => setF({ ...f, assignees: f.assignees.includes(id) ? f.assignees.filter((x) => x !== id) : [...f.assignees, id] });
  const addFiles = (e) => { setFiles([...files, ...Array.from(e.target.files || [])].slice(0, 5)); e.target.value = ""; };
  const submit = async (e) => {
    e.preventDefault();
    if (!f.assignees.length) { toast.error("Pilih minimal satu admin"); return; }
    setSaving(true);
    try {
      const payload = { ...f, deadline: f.deadline ? new Date(f.deadline).toISOString() : "" };
      const r = editing ? await api.put(`/admin-tasks/${task.id}`, payload) : await api.post("/admin-tasks", payload);
      const tid = r.data?.id;
      for (const file of files) {
        const fd = new FormData(); fd.append("file", file);
        await api.post(`/admin-tasks/${tid}/attachment`, fd, { headers: { "Content-Type": "multipart/form-data" } }).catch((err) => toast.error(`Lampiran ${file.name}: ${errMsg(err)}`));
      }
      toast.success(editing ? "Tugas diperbarui" : "Tugas dikirim ke admin"); setF(EMPTY); setFiles([]); onCreated();
    } catch (err) { toast.error(errMsg(err)); } finally { setSaving(false); }
  };
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg" data-testid="task-create-dialog">
        <DialogHeader><DialogTitle>{editing ? "Edit Tugas Admin" : "Buat Tugas Admin"}</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div className="space-y-1"><Label>Judul tugas</Label><Input value={f.title} onChange={(e) => setF({ ...f, title: e.target.value })} placeholder="mis. Cek semua router offline di Kab. Ketapang" required data-testid="task-title" /></div>
          <div className="space-y-1"><Label>Rincian / memo</Label><Textarea rows={4} value={f.detail} onChange={(e) => setF({ ...f, detail: e.target.value })} placeholder="Langkah yang harus dikerjakan, cabang/agen terkait, hasil yang diharapkan..." data-testid="task-detail" /></div>
          <div className="space-y-1">
            <Label>Lampiran pekerjaan (foto/PDF, maks 5){editing && task.attachments?.length ? ` — sudah ada ${task.attachments.length}` : ""}</Label>
            <div className="flex flex-wrap gap-1.5 items-center" data-testid="task-attachments">
              {files.map((file, i) => (
                <span key={i} className="flex items-center gap-1 text-xs px-2 py-1 rounded-md border border-border max-w-56" data-testid={`task-attachment-${i}`}>
                  <Paperclip className="w-3 h-3 shrink-0" /><span className="truncate">{file.name}</span>
                  <button type="button" onClick={() => setFiles(files.filter((_, j) => j !== i))} className="text-muted-foreground hover:text-rose-400" data-testid={`task-attachment-remove-${i}`}><X className="w-3 h-3" /></button>
                </span>
              ))}
              <Button type="button" size="sm" variant="outline" className="h-7 text-xs" onClick={() => fileRef.current?.click()} disabled={files.length >= 5} data-testid="task-attachment-btn"><Paperclip className="w-3 h-3 mr-1" /> Tambah lampiran</Button>
              <input ref={fileRef} type="file" multiple accept="image/*,application/pdf" className="hidden" onChange={addFiles} data-testid="task-attachment-input" />
            </div>
          </div>
          <div className="space-y-1">
            <Label>Ditugaskan ke</Label>
            <div className="flex flex-wrap gap-2" data-testid="task-assignees">
              {admins.map((a) => (
                <label key={a.id} className={`flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg border text-xs cursor-pointer ${f.assignees.includes(a.id) ? "border-[#F04E23] bg-[#F04E23]/10" : "border-border"}`}>
                  <Checkbox checked={f.assignees.includes(a.id)} onCheckedChange={() => toggle(a.id)} data-testid={`task-assignee-${a.id}`} /> {a.name}
                </label>
              ))}
              {!admins.length && <span className="text-xs text-muted-foreground">Belum ada admin lain.</span>}
            </div>
          </div>
          <div className="grid grid-cols-2 gap-3">
            <div className="space-y-1"><Label>Target selesai (tanggal & jam)</Label><Input type="datetime-local" value={f.deadline} onChange={(e) => setF({ ...f, deadline: e.target.value })} className="cursor-pointer" onClick={(e) => e.target.showPicker?.()} data-testid="task-deadline" /></div>
            <div className="space-y-1"><Label>Prioritas</Label>
              <select className="h-10 w-full rounded-md border border-input bg-background px-2 text-sm" value={f.priority} onChange={(e) => setF({ ...f, priority: e.target.value })} data-testid="task-priority">
                {PRIORITIES.map(([v, l]) => <option key={v} value={v}>{l}</option>)}
              </select>
            </div>
          </div>
          <Button type="submit" disabled={saving} className="w-full bg-[#F04E23] hover:bg-[#d8431c] text-white" data-testid="task-create-submit">{saving ? "Menyimpan..." : editing ? "Simpan Perubahan" : "Kirim Tugas"}</Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
